/**
 * AdminReportsPage — unified reporting hub for admin.
 *
 * Combines:
 *   - Mongo-backed metrics: hero leads / free-giveaway leads / registrations
 *     / paid users / abandoned Razorpay checkouts (grouped by IST day).
 *   - GA4 Data API: traffic, top landing pages, engagement rate, device /
 *     source / medium / country breakdowns, daily trend.
 *
 * Layout: unified overview at top, sub-tabs beneath for drill-downs.
 * Global filter bar (date range + region + device + source + medium)
 * updates every widget on the page.
 *
 * All timestamps are treated as IST calendar days — see the backend.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, AlertTriangle, Check, TrendingUp,
  Users, MousePointerClick, CreditCard, XCircle, UserPlus, ExternalLink,
  Info, Globe, Smartphone, Route as RouteIcon, FlaskConical,
} from 'lucide-react';
import { AppShell } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { api } from '../lib/api';

const IST_LABEL = 'IST · Asia/Kolkata';

// Format YYYY-MM-DD in IST (the timezone the property is set to).
function istDateStr(d) {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric', month: '2-digit', day: '2-digit',
  });
  return fmt.format(d);
}

const RANGE_PRESETS = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '14', label: 'Last 14 days', days: 14 },
  { key: '28', label: 'Last 28 days', days: 28 },
  { key: '90', label: 'Last 90 days', days: 90 },
];

// Small helpers for rendering.
function formatNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M';
  if (v >= 1_000) return (v / 1_000).toFixed(v >= 10_000 ? 0 : 1) + 'k';
  return v.toLocaleString('en-IN');
}
function formatPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `${(Number(n) * 100).toFixed(1)}%`;
}
function formatCurrency(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

// ---------- Sub-components ----------

function KpiTile({ icon: Icon, label, value, sub, tone = 'default', testid }) {
  const toneCls = {
    default: 'text-slate-700 dark:text-slate-200',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-500',
    sky: 'text-sky-600 dark:text-sky-400',
    violet: 'text-violet-600 dark:text-violet-400',
  }[tone] || 'text-slate-700';
  return (
    <Card className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm rounded-2xl" data-testid={testid}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground truncate">{label}</p>
            <p className={`mt-1.5 text-2xl sm:text-3xl font-bold tabular-nums leading-none ${toneCls}`}>{value}</p>
            {sub && <p className="mt-1.5 text-[12px] text-slate-500 dark:text-slate-400 leading-snug">{sub}</p>}
          </div>
          {Icon && (
            <span className={`shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 ${toneCls}`}>
              <Icon size={16} />
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SectionCard({ title, description, children, testid, right }) {
  return (
    <Card className="border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-sm rounded-2xl" data-testid={testid}>
      <CardContent className="p-5 sm:p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.2em] uppercase text-muted-foreground">Report</p>
            <h3 className="mt-1 text-[15px] sm:text-base font-semibold text-slate-900 dark:text-slate-50">{title}</h3>
            {description && <p className="text-[12.5px] text-slate-500 dark:text-slate-400 mt-0.5">{description}</p>}
          </div>
          {right}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * Horizontal bar list — used for landing pages, sources, devices, countries.
 * data = [{label, value, secondary?}]
 */
function RankedBars({ data, valueLabel = 'Sessions', emptyText = 'No data in this range.', testid }) {
  const max = data && data.length > 0 ? Math.max(...data.map((d) => Number(d.value) || 0)) : 0;
  if (!data || data.length === 0) {
    return <p className="text-[13px] text-slate-500 italic" data-testid={`${testid}-empty`}>{emptyText}</p>;
  }
  return (
    <ul className="space-y-2.5" data-testid={testid}>
      {data.slice(0, 12).map((row, i) => {
        const pct = max > 0 ? Math.max(2, (Number(row.value) / max) * 100) : 0;
        return (
          <li key={i} className="min-w-0">
            <div className="flex items-center gap-3">
              <p className="text-[13px] text-slate-800 dark:text-slate-100 flex-1 min-w-0 truncate" title={row.label}>{row.label || '(none)'}</p>
              <p className="shrink-0 tabular-nums text-[13px] font-semibold text-slate-700 dark:text-slate-200">{formatNum(row.value)}</p>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div className="h-full bg-primary/80 transition-all" style={{ width: `${pct}%` }} />
            </div>
            {row.secondary != null && (
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{valueLabel}: {formatNum(row.secondary)}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// Compact SVG spark-bar chart from daily counts.
function SparkBars({ daily, tone = 'primary' }) {
  if (!daily || daily.length === 0) {
    return <p className="text-[12px] text-slate-500 italic mt-2">No activity in this range.</p>;
  }
  const max = Math.max(...daily.map((d) => Number(d.count) || 0), 1);
  const barTone = {
    primary: 'bg-primary/80',
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    sky: 'bg-sky-500',
    red: 'bg-red-500',
  }[tone] || 'bg-primary/80';
  return (
    <div className="mt-3 flex items-end gap-0.5 h-14">
      {daily.map((d) => {
        const h = (Number(d.count) / max) * 100;
        return (
          <div
            key={d.date}
            className="flex-1 rounded-t-sm min-h-[3px] transition-all"
            style={{ height: `${Math.max(6, h)}%` }}
            title={`${d.date}: ${d.count}`}
          >
            <div className={`w-full h-full ${barTone} rounded-t-sm`} />
          </div>
        );
      })}
    </div>
  );
}

function FilterBar({ range, setRange, filters, setFilters, onRefresh, loading, ga4Connected }) {
  const setPreset = (days) => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - days);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    setRange({ start: istDateStr(start), end: istDateStr(yesterday) });
  };
  const clearFilters = () => setFilters({ country: '', device: '', source: '', medium: '' });
  const hasFilters = filters.country || filters.device || filters.source || filters.medium;
  return (
    <div
      className="sticky top-4 z-10 rounded-2xl border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md shadow-sm p-4 sm:p-5"
      data-testid="admin-reports-filter-bar"
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">Start · IST</label>
          <input
            type="date"
            value={range.start}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
            className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
            data-testid="admin-reports-start"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">End · IST</label>
          <input
            type="date"
            value={range.end}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
            className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
            data-testid="admin-reports-end"
          />
        </div>
        <div className="hidden sm:flex flex-col gap-1">
          <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">Presets</label>
          <div className="flex gap-1">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setPreset(p.days)}
                className="text-[12px] rounded-full border border-slate-200 dark:border-slate-700 px-2.5 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                data-testid={`admin-reports-preset-${p.key}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-[11.5px] rounded-full border px-2.5 py-1 ${
            ga4Connected
              ? 'border-emerald-300/60 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-300'
              : 'border-amber-300/60 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300'
          }`}>
            {ga4Connected ? <Check size={11} /> : <AlertTriangle size={11} />}
            GA4 {ga4Connected ? 'live' : 'not connected'}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            disabled={loading}
            className="rounded-full h-9"
            data-testid="admin-reports-refresh"
          >
            {loading ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <RefreshCw size={13} className="mr-1.5" />}
            Refresh
          </Button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <FilterField label="Country" value={filters.country} onChange={(v) => setFilters((f) => ({ ...f, country: v }))} placeholder="e.g. India" testid="admin-reports-filter-country" />
        <FilterField label="Device" value={filters.device} onChange={(v) => setFilters((f) => ({ ...f, device: v }))} placeholder="mobile / desktop / tablet" testid="admin-reports-filter-device" />
        <FilterField label="Source" value={filters.source} onChange={(v) => setFilters((f) => ({ ...f, source: v }))} placeholder="google, direct, …" testid="admin-reports-filter-source" />
        <FilterField label="Medium" value={filters.medium} onChange={(v) => setFilters((f) => ({ ...f, medium: v }))} placeholder="cpc, organic, referral" testid="admin-reports-filter-medium" />
        {hasFilters && (
          <Button size="sm" variant="ghost" onClick={clearFilters} className="rounded-full h-9 self-end text-[12px]" data-testid="admin-reports-clear-filters">
            Clear filters
          </Button>
        )}
        <p className="text-[11px] text-slate-500 ml-auto pb-1.5 flex items-center gap-1.5">
          <Info size={11} /> {IST_LABEL}
        </p>
      </div>
    </div>
  );
}

function FilterField({ label, value, onChange, placeholder, testid }) {
  return (
    <div className="flex flex-col gap-1 min-w-[140px]">
      <label className="text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
        data-testid={testid}
      />
    </div>
  );
}

function Ga4NotConnectedCard({ reason }) {
  const enableUrl = 'https://console.cloud.google.com/apis/library/analyticsdata.googleapis.com';
  return (
    <Card className="border border-amber-300/70 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/20 rounded-2xl shadow-sm" data-testid="admin-reports-ga4-disconnected">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <span className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/15 text-amber-700 dark:text-amber-400">
            <AlertTriangle size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-semibold text-amber-900 dark:text-amber-200">GA4 Data API isn&rsquo;t connected yet</p>
            <p className="mt-1 text-[13.5px] leading-relaxed text-amber-800 dark:text-amber-300 max-w-[70ch]">
              {reason || 'The credentials look valid but the API is not enabled on the Google Cloud project.'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={enableUrl}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white px-3.5 py-1.5 text-[12.5px] font-medium transition-colors"
                data-testid="admin-reports-ga4-enable-link"
              >
                Enable Data API <ExternalLink size={12} />
              </a>
              <span className="text-[12px] text-amber-800/80 dark:text-amber-300/80 self-center">
                Then click Refresh above — GA4 widgets light up automatically.
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- A/B Test panel ----------

// Headline copy for each experiment — surfaces in the panel so admin
// remembers what "long" vs "short" actually rendered on the page.
const AB_EXPERIMENTS = [
  {
    key: 'recruiter_headline_v1',
    label: 'Recruiter hero headline',
    page: '/recruiter-visibility',
    variants: {
      long: 'Résumé and LinkedIn makeovers for more interviews and better opportunities.',
      short: 'More interviews. Better opportunities.',
    },
  },
];

function AbTestPanel({ range }) {
  const [experimentKey, setExperimentKey] = useState(AB_EXPERIMENTS[0].key);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const experiment = AB_EXPERIMENTS.find((e) => e.key === experimentKey) || AB_EXPERIMENTS[0];

  const load = useCallback(async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        experiment: experimentKey,
        date_from: range.start,
        date_to: range.end,
      });
      const r = await api.get(`/admin/reports/ab-test?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load A/B report.';
      setError(typeof msg === 'string' ? msg : 'Failed to load A/B report.');
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, experimentKey]);

  useEffect(() => { load(); }, [load]);

  const variants = data?.variants || [];
  const total = data?.total || {};
  const winnerVariant = variants.length
    ? variants.reduce((best, v) => (
        (v.click_to_purchase_pct || 0) > (best.click_to_purchase_pct || 0) ? v : best
      ), variants[0])
    : null;

  return (
    <div className="space-y-4" data-testid="admin-reports-panel-ab">
      <SectionCard
        title="Experiment"
        description="Only rows with an assigned variant are counted."
        testid="section-ab-picker"
        right={<FlaskConical size={14} className="text-muted-foreground" />}
      >
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={experimentKey}
            onChange={(e) => setExperimentKey(e.target.value)}
            className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-[13px] focus:ring-2 focus:ring-primary/40 focus:outline-none"
            data-testid="admin-ab-experiment-select"
          >
            {AB_EXPERIMENTS.map((e) => (
              <option key={e.key} value={e.key}>
                {e.label} · {e.page}
              </option>
            ))}
          </select>
          {loading && <Loader2 size={13} className="animate-spin text-muted-foreground" />}
        </div>
        {error && (
          <p className="mt-3 text-[12.5px] text-red-600" data-testid="admin-ab-error">{error}</p>
        )}
      </SectionCard>

      {variants.length === 0 && !loading && !error && (
        <SectionCard title="No variant activity yet" testid="section-ab-empty">
          <p className="text-[13px] text-slate-500 italic">
            No payment events with an assigned variant in this range. Once visitors on
            <code className="mx-1 px-1 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-[12px]">{experiment.page}</code>
            click the CTA, they will appear here — split by the sticky variant they were bucketed into.
          </p>
        </SectionCard>
      )}

      {variants.length > 0 && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              icon={MousePointerClick}
              label="Total clicks"
              value={formatNum(total.click)}
              sub="Across all variants"
              tone="sky"
              testid="ab-kpi-clicks"
            />
            <KpiTile
              icon={CreditCard}
              label="Total purchases"
              value={formatNum(total.checkout_completed)}
              sub={total.click ? `${((total.checkout_completed / total.click) * 100).toFixed(1)}% click-to-buy` : '—'}
              tone="emerald"
              testid="ab-kpi-purchases"
            />
            <KpiTile
              icon={XCircle}
              label="Abandoned"
              value={formatNum((total.checkout_abandoned || 0) + (total.checkout_failed || 0))}
              sub="Dismissed + failed"
              tone="red"
              testid="ab-kpi-abandoned"
            />
            <KpiTile
              icon={TrendingUp}
              label="Revenue"
              value={total.revenue_paise ? formatCurrency(Number(total.revenue_paise) / 100) : '—'}
              sub="Sum of completed"
              tone="violet"
              testid="ab-kpi-revenue"
            />
          </div>

          <SectionCard
            title="Conversion by variant"
            description="Click → purchase, per variant. Higher click-to-buy % is winning."
            testid="section-ab-variants"
          >
            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-[13px]" data-testid="ab-variants-table">
                <thead>
                  <tr className="text-left text-[10.5px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">
                    <th className="py-2 pr-3">Variant</th>
                    <th className="py-2 pr-3 text-right">Clicks</th>
                    <th className="py-2 pr-3 text-right">Started</th>
                    <th className="py-2 pr-3 text-right">Completed</th>
                    <th className="py-2 pr-3 text-right">Abandoned</th>
                    <th className="py-2 pr-3 text-right">Click → Buy</th>
                    <th className="py-2 pr-3 text-right">Start → Buy</th>
                    <th className="py-2 pr-3 text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200/70 dark:divide-slate-800">
                  {variants.map((v) => {
                    const isWinner = winnerVariant && v.variant === winnerVariant.variant && (v.click_to_purchase_pct || 0) > 0;
                    return (
                      <tr key={v.variant} data-testid={`ab-variant-row-${v.variant}`}>
                        <td className="py-3 pr-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={isWinner ? 'default' : 'secondary'}
                              className="uppercase text-[10.5px]"
                              data-testid={`ab-variant-badge-${v.variant}`}
                            >
                              {v.variant}
                            </Badge>
                            {isWinner && (
                              <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                                winning
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-[11.5px] text-slate-500 dark:text-slate-400 max-w-[46ch]">
                            {experiment.variants[v.variant] || '—'}
                          </p>
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums">{formatNum(v.click)}</td>
                        <td className="py-3 pr-3 text-right tabular-nums">{formatNum(v.checkout_started)}</td>
                        <td className="py-3 pr-3 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
                          {formatNum(v.checkout_completed)}
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums">
                          {formatNum((v.checkout_abandoned || 0) + (v.checkout_failed || 0))}
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums font-semibold">
                          {v.click_to_purchase_pct != null ? `${v.click_to_purchase_pct.toFixed(2)}%` : '—'}
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums">
                          {v.start_to_purchase_pct != null ? `${v.start_to_purchase_pct.toFixed(2)}%` : '—'}
                        </td>
                        <td className="py-3 pr-3 text-right tabular-nums">
                          {v.revenue_paise ? formatCurrency(Number(v.revenue_paise) / 100) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-4">
              <RankedBars
                data={variants.map((v) => ({
                  label: `${v.variant} — ${v.click_to_purchase_pct?.toFixed(2) || '0.00'}%`,
                  value: v.checkout_completed || 0,
                  secondary: v.click || 0,
                }))}
                valueLabel="Clicks"
                testid="ab-variants-bars"
              />
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}


// ---------- Main page ----------

export default function AdminReportsPage() {
  const navigate = useNavigate();
  const [range, setRange] = useState({ start: '', end: '' });
  const [filters, setFilters] = useState({ country: '', device: '', source: '', medium: '' });
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');

  // Prime the range from the backend's default (last 7 days ending yesterday, IST).
  useEffect(() => {
    api.get('/admin/reports/status')
      .then((r) => {
        setStatus(r.data);
        if (r.data?.defaultRange) setRange(r.data.defaultRange);
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setError('You need to be an admin to view this page.');
        }
      });
  }, []);

  const load = useCallback(async () => {
    if (!range.start || !range.end) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ start: range.start, end: range.end });
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
      const r = await api.get(`/admin/reports/overview?${params.toString()}`);
      setData(r.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to load report.';
      setError(typeof msg === 'string' ? msg : 'Failed to load report.');
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end, filters]);

  useEffect(() => { load(); }, [load]);

  const mongo = data?.mongo || {};
  const ga4 = data?.ga4 || {};
  const ga4Connected = ga4?.connected === true;
  const totals = ga4?.totals || {};
  const paymentFunnel = mongo?.paymentFunnel || {};

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-reports-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="rounded-full" data-testid="admin-reports-back">
            <ArrowLeft size={14} className="mr-1.5" /> Admin
          </Button>
          <div>
            <p className="text-[10.5px] font-mono-edge tracking-[0.22em] uppercase text-muted-foreground">Reports</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-50 leading-tight">
              Growth reporting hub
            </h1>
          </div>
          {status?.ga4?.client_email && (
            <span className="hidden md:inline-flex items-center gap-1.5 ml-auto text-[11px] text-slate-500 dark:text-slate-400 truncate">
              <Info size={11} /> {status.ga4.client_email}
            </span>
          )}
        </div>

        {/* Global filter bar */}
        <FilterBar
          range={range}
          setRange={setRange}
          filters={filters}
          setFilters={setFilters}
          onRefresh={load}
          loading={loading}
          ga4Connected={ga4Connected}
        />

        {error && (
          <div className="mt-5 rounded-xl border border-red-300/60 bg-red-50 dark:bg-red-950/20 dark:border-red-900/50 text-red-700 dark:text-red-300 px-4 py-3 text-[13.5px]" data-testid="admin-reports-error">
            {error}
          </div>
        )}

        {/* KPI tiles — always visible */}
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6" data-testid="admin-reports-kpis">
          <KpiTile
            icon={Users}
            label="Traffic (users)"
            value={ga4Connected ? formatNum(totals.activeUsers) : '—'}
            sub={ga4Connected ? `${formatNum(totals.sessions)} sessions` : 'Connect GA4 to see'}
            tone="sky"
            testid="kpi-traffic"
          />
          <KpiTile
            icon={TrendingUp}
            label="Engagement rate"
            value={ga4Connected ? formatPct(totals.engagementRate) : '—'}
            sub={ga4Connected ? `${formatNum(totals.eventCount)} events` : ''}
            tone="emerald"
            testid="kpi-engagement"
          />
          <KpiTile
            icon={MousePointerClick}
            label="Free leads"
            value={formatNum(mongo?.freeGiveawayLeads?.total)}
            sub={`${formatNum(mongo?.heroLeads?.total)} hero leads`}
            tone="violet"
            testid="kpi-free-leads"
          />
          <KpiTile
            icon={UserPlus}
            label="Registrations"
            value={formatNum(mongo?.registrations?.total)}
            sub="New users this range"
            testid="kpi-registrations"
          />
          <KpiTile
            icon={CreditCard}
            label="Paid users"
            value={formatNum(paymentFunnel.paid)}
            sub={`from ${formatNum(paymentFunnel.started)} checkouts`}
            tone="emerald"
            testid="kpi-paid"
          />
          <KpiTile
            icon={XCircle}
            label="Abandoned rate"
            value={formatPct(paymentFunnel.abandonedRate)}
            sub={`${formatNum(paymentFunnel.abandoned)} abandoned`}
            tone="red"
            testid="kpi-abandoned"
          />
        </div>

        {/* GA4 connect banner */}
        {!ga4Connected && (
          <div className="mt-5">
            <Ga4NotConnectedCard reason={ga4?.reason} />
          </div>
        )}

        {/* Sub-tabs drill-down */}
        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="bg-secondary rounded-xl p-1 flex-wrap h-auto" data-testid="admin-reports-tabs">
            <TabsTrigger value="overview" className="rounded-lg" data-testid="admin-reports-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="traffic" className="rounded-lg" data-testid="admin-reports-tab-traffic">Traffic</TabsTrigger>
            <TabsTrigger value="leads" className="rounded-lg" data-testid="admin-reports-tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="funnel" className="rounded-lg" data-testid="admin-reports-tab-funnel">Funnel</TabsTrigger>
            <TabsTrigger value="ab" className="rounded-lg" data-testid="admin-reports-tab-ab">A/B</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-5 space-y-4" data-testid="admin-reports-panel-overview">
            <div className="grid gap-4 lg:grid-cols-2">
              <SectionCard title="Top landing pages" description="Where visitors first arrive" testid="section-landing-pages">
                {ga4Connected ? (
                  <RankedBars
                    data={(ga4.landingPages || []).map((r) => ({ label: r.path, value: r.sessions, secondary: r.activeUsers }))}
                    valueLabel="Users"
                    testid="landing-pages-bars"
                  />
                ) : (
                  <p className="text-[13px] text-slate-500 italic">Enable GA4 to see landing page data.</p>
                )}
              </SectionCard>
              <SectionCard title="By source / medium" description="Where the traffic came from" testid="section-source">
                {ga4Connected ? (
                  <RankedBars
                    data={(ga4.bySource || []).map((r) => ({
                      label: `${r.sessionSource || '(direct)'} · ${r.sessionMedium || '(none)'}`,
                      value: r.activeUsers,
                      secondary: r.sessions,
                    }))}
                    valueLabel="Sessions"
                    testid="source-bars"
                  />
                ) : (
                  <p className="text-[13px] text-slate-500 italic">Enable GA4 to see attribution.</p>
                )}
              </SectionCard>
            </div>
            <SectionCard title="Daily activity — leads, payments, GA4 users" description="Trend across the selected range (IST calendar days)" testid="section-daily">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <DailyPanel label="Free leads" tone="violet" daily={mongo?.freeGiveawayLeads?.daily} />
                <DailyPanel label="Hero leads" tone="sky" daily={mongo?.heroLeads?.daily} />
                <DailyPanel label="Payments" tone="emerald" daily={paymentFunnel?.daily} />
                <DailyPanel label="GA4 users" tone="primary" daily={(ga4.byDay || []).map((r) => ({ date: r.date, count: r.activeUsers }))} />
              </div>
            </SectionCard>
          </TabsContent>

          {/* Traffic tab */}
          <TabsContent value="traffic" className="mt-5 space-y-4" data-testid="admin-reports-panel-traffic">
            {!ga4Connected ? (
              <Ga4NotConnectedCard reason={ga4?.reason} />
            ) : (
              <>
                <div className="grid gap-4 lg:grid-cols-2">
                  <SectionCard title="By device" testid="section-device" right={<Smartphone size={14} className="text-muted-foreground" />}>
                    <RankedBars
                      data={(ga4.byDevice || []).map((r) => ({ label: r.deviceCategory, value: r.activeUsers, secondary: r.sessions }))}
                      valueLabel="Sessions"
                      testid="device-bars"
                    />
                  </SectionCard>
                  <SectionCard title="By country" testid="section-country" right={<Globe size={14} className="text-muted-foreground" />}>
                    <RankedBars
                      data={(ga4.byCountry || []).map((r) => ({ label: r.country, value: r.activeUsers }))}
                      valueLabel="Users"
                      testid="country-bars"
                    />
                  </SectionCard>
                </div>
                <SectionCard title="Top landing pages (full)" description="Sorted by sessions" testid="section-landing-pages-full">
                  <RankedBars
                    data={(ga4.landingPages || []).map((r) => ({ label: r.path, value: r.sessions, secondary: r.activeUsers }))}
                    valueLabel="Users"
                    testid="landing-pages-full-bars"
                  />
                </SectionCard>
              </>
            )}
          </TabsContent>

          {/* Leads tab */}
          <TabsContent value="leads" className="mt-5 space-y-4" data-testid="admin-reports-panel-leads">
            <div className="grid gap-4 lg:grid-cols-2">
              <SectionCard title="Free-giveaway leads by source" description="Popup captures vs /free page vs unknown" testid="section-free-by-source">
                <RankedBars
                  data={(mongo?.freeGiveawayLeads?.bySource || []).map((r) => ({ label: r.source || '(unknown)', value: r.count }))}
                  valueLabel="Leads"
                  testid="free-by-source-bars"
                />
                <div className="mt-4 pt-4 border-t border-slate-200/70 dark:border-slate-800 flex items-center gap-3">
                  <Button size="sm" variant="outline" onClick={() => navigate('/admin/free-leads')} className="rounded-full" data-testid="jump-free-leads">
                    Open lead table <RouteIcon size={12} className="ml-1.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/admin/hero-leads')} className="rounded-full" data-testid="jump-hero-leads">
                    Hero leads <RouteIcon size={12} className="ml-1.5" />
                  </Button>
                </div>
              </SectionCard>
              <SectionCard title="Free-score email captures" description="Score preview / free landing signups" testid="section-free-score-leads">
                <p className="text-3xl font-bold tabular-nums">{formatNum(mongo?.freeScoreLeads?.total)}</p>
                <p className="text-[12.5px] text-slate-500 mt-1">leads in this range</p>
              </SectionCard>
            </div>
            <SectionCard title="Daily lead flow (IST)" testid="section-lead-daily">
              <div className="grid gap-4 sm:grid-cols-2">
                <DailyPanel label="Free-giveaway leads" tone="violet" daily={mongo?.freeGiveawayLeads?.daily} />
                <DailyPanel label="Hero leads" tone="sky" daily={mongo?.heroLeads?.daily} />
              </div>
            </SectionCard>
          </TabsContent>

          {/* Funnel tab */}
          <TabsContent value="funnel" className="mt-5 space-y-4" data-testid="admin-reports-panel-funnel">
            <div className="grid gap-4 lg:grid-cols-3">
              <KpiTile icon={MousePointerClick} label="Checkouts started" value={formatNum(paymentFunnel.started)} sub="Razorpay checkout opened" tone="sky" testid="funnel-started" />
              <KpiTile icon={CreditCard} label="Payments succeeded" value={formatNum(paymentFunnel.paid)} sub="Order marked paid" tone="emerald" testid="funnel-paid" />
              <KpiTile icon={XCircle} label="Abandoned" value={formatNum(paymentFunnel.abandoned)} sub={`Rate ${formatPct(paymentFunnel.abandonedRate)}`} tone="red" testid="funnel-abandoned" />
            </div>
            <SectionCard title="Revenue by plan" description="From successful payment events" testid="section-funnel-plans">
              {(paymentFunnel.byPlan || []).length === 0 ? (
                <p className="text-[13px] text-slate-500 italic">No successful payments in this range.</p>
              ) : (
                <ul className="divide-y divide-slate-200/70 dark:divide-slate-800">
                  {(paymentFunnel.byPlan || []).map((p, i) => (
                    <li key={i} className="py-3 first:pt-0 last:pb-0 flex flex-wrap items-center gap-3">
                      <Badge variant="secondary" className="uppercase text-[10.5px]">{p.plan}</Badge>
                      <p className="text-[13px] text-slate-700 dark:text-slate-200 flex-1">{p.count} payment{p.count === 1 ? '' : 's'}</p>
                      <p className="text-[13px] font-semibold tabular-nums">{formatCurrency(p.revenue)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
            <SectionCard title="Daily payment activity" testid="section-funnel-daily">
              <DailyPanel label="Payment events per day" tone="emerald" daily={paymentFunnel.daily} full />
            </SectionCard>
          </TabsContent>

          {/* A/B tab */}
          <TabsContent value="ab" className="mt-5" data-testid="admin-reports-panel-ab-wrapper">
            <AbTestPanel range={range} />
          </TabsContent>
        </Tabs>

        {loading && !data && (
          <div className="mt-8 flex items-center justify-center text-[13px] text-slate-500 gap-2" data-testid="admin-reports-initial-loading">
            <Loader2 size={14} className="animate-spin" /> Loading reports…
          </div>
        )}
      </div>
    </AppShell>
  );
}

function DailyPanel({ label, tone, daily, full }) {
  const total = (daily || []).reduce((sum, d) => sum + Number(d.count || 0), 0);
  return (
    <div className={`rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-3 ${full ? 'col-span-full' : ''}`}>
      <p className="text-[10.5px] font-mono-edge tracking-[0.16em] uppercase text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums text-slate-900 dark:text-slate-50">{formatNum(total)}</p>
      <SparkBars daily={daily} tone={tone} />
    </div>
  );
}
